import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import client from './api/client';
import { USER_URL, itemsUrl, preferencesUrl, getCached, fetchCached } from './api/destinationCache';
import PageHeader from './components/PageHeader';
import SeedingPreferences from './components/SeedingPreferences';
import DataRefreshControls from './components/DataRefreshControls';
import ItemStockingTable from './components/ItemStockingTable';
import ItemAnalysisSection from './components/ItemAnalysisSection';
import CollapsiblePanel from './components/CollapsiblePanel';
import RegionMarketStatsTable from './components/RegionMarketStatsTable';
import RegionIskVolumeSection from './components/RegionIskVolumeSection';
import MarkupOpportunitiesSection from './components/MarkupOpportunitiesSection';
import AddItemModal from './components/AddItemModal';
import { formatPrice } from './utils/formatPrice';
import { calculateROI as calcROI, filterItems, nextSortConfig, sortItems } from './utils/seedingMath';
import { STRUCTURE_SCC_SURCHARGE } from './utils/eveConstants';
import './App.css';
import './SeedingPage.css';

function SeedingDetailPage() {
  const { destinationId } = useParams();
  const navigate = useNavigate();

  // Authentication state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Seeding data state
  const [destination, setDestination] = useState(null);
  const [seedingItems, setSeedingItems] = useState([]);
  const [error, setError] = useState('');

  // Add item modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [targetStock, setTargetStock] = useState(0);
  const [addingItem, setAddingItem] = useState(false);

  // Edit target stock state
  const [editingStock, setEditingStock] = useState(null);
  const [editStockValue, setEditStockValue] = useState('');

  // Refresh state
  const [refreshingMarketData, setRefreshingMarketData] = useState(false);
  const [refreshingUserOrders, setRefreshingUserOrders] = useState(false);
  const [refreshingWalletTransactions, setRefreshingWalletTransactions] = useState(false);
  const [refreshingRegionStats, setRefreshingRegionStats] = useState(false);
  const [refreshingAllRegionTypes, setRefreshingAllRegionTypes] = useState(false);
  // refreshTimers state lives in DataRefreshControls to avoid full-page re-renders every second

  // Progress tracking state
  const [progressData, setProgressData] = useState({
    marketData: null,
    userOrders: null,
    walletTransactions: null,
    regionStats: null,
    allRegionTypes: null,
  });
  const pollingIntervalsRef = useRef({
    marketData: null,
    userOrders: null,
    walletTransactions: null,
    regionStats: null,
    allRegionTypes: null,
  });

  // Region Market Overview state
  const [regionOverviewCollapsed, setRegionOverviewCollapsed] = useState(true);
  const [regionOverviewData, setRegionOverviewData] = useState(null);
  const [regionOverviewLoading, setRegionOverviewLoading] = useState(false);
  const [regionOverviewDays, setRegionOverviewDays] = useState(30);
  const [regionOverviewSearch, setRegionOverviewSearch] = useState('');
  const [regionOverviewDebouncedSearch, setRegionOverviewDebouncedSearch] = useState('');
  const [regionOverviewSort, setRegionOverviewSort] = useState({ col: 'avg_volume', order: 'desc' });
  const [regionOverviewPage, setRegionOverviewPage] = useState(1);

  // Collateral state
  const [collateralPercentage, setCollateralPercentage] = useState(0);
  const [iskPerM3, setIskPerM3] = useState(0);
  const [brokerFeePercentage, setBrokerFeePercentage] = useState(0);
  const [salesTaxPercentage, setSalesTaxPercentage] = useState(0);
  const [desiredRoiPercentage, setDesiredRoiPercentage] = useState(10);
  const [daysBackForPurchases, setDaysBackForPurchases] = useState(30);
  const [transactionTimeWindowMinutes, setTransactionTimeWindowMinutes] = useState(2);
  const [analysisPeriod, setAnalysisPeriod] = useState(60);
  const [aggregatePeriod, setAggregatePeriod] = useState(7);
  const [restockCadenceDays, setRestockCadenceDays] = useState(7);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  // The preference values last loaded from or saved to the server, so unchanged values aren't re-saved
  const savedPreferencesRef = useRef(null);

  // State to trigger refresh of seeding items after preferences update
  const [shouldRefreshItems, setShouldRefreshItems] = useState(0);

  // Purchase cluster selector state
  const [showClusterModal, setShowClusterModal] = useState(false);
  const [clusters, setClusters] = useState([]);
  const [pinnedClusterDate, setPinnedClusterDate] = useState(null);
  const [loadingClusters, setLoadingClusters] = useState(false);

  // State for collapsible seeding items table
  const [seedingItemsCollapsed, setSeedingItemsCollapsed] = useState(false);

  // State for collapsible item analysis section
  const [itemAnalysisCollapsed, setItemAnalysisCollapsed] = useState(false);
  const [analysisItemId, setAnalysisItemId] = useState(null);

  // State for expandable table rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Forecast cache at page level so it persists across table collapse/expand
  const [itemForecasts, setItemForecasts] = useState({});
  const [loadingForecasts, setLoadingForecasts] = useState(new Set());

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'profit_percentage', direction: 'desc' });

  // Filter state; by default show profitable items that are running low.
  // roiMin starts as null: the floor follows the Target ROI setting until the user types their own.
  const [filterState, setFilters] = useState({
    purchasedPrice: 'all', // 'all', 'with', 'without'
    roiMin: null,
    roiMax: '',
    inStockMin: '',
    inStockMax: '10'
  });
  const filters = useMemo(
    () => ({ ...filterState, roiMin: filterState.roiMin ?? String(desiredRoiPercentage) }),
    [filterState, desiredRoiPercentage]
  );

  // Item stocking page, kept here because the table unmounts while items reload
  const [itemPage, setItemPage] = useState(1);
  useEffect(() => setItemPage(1), [filters, sortConfig]);

  const applyUserInfo = useCallback((data) => {
    setUser(data.authenticated ? data.character : null);
  }, []);

  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await fetchCached(USER_URL);
      applyUserInfo(response.data);
    } catch (error) {
      console.error('Error fetching user info:', error);
      setUser(null);
    }
  }, [applyUserInfo]);

  // Player structures add the SCC surcharge on top of sales tax
  const sccPercentage = destination?.is_structure ? STRUCTURE_SCC_SURCHARGE : 0;
  const calculateROI = useCallback(
    (item) => calcROI(item, { collateralPercentage, iskPerM3, brokerFeePercentage, salesTaxPercentage, sccPercentage }),
    [collateralPercentage, iskPerM3, brokerFeePercentage, salesTaxPercentage, sccPercentage]
  );

  const applySeedingPreferences = useCallback((data) => {
    if (data.success) {
      const prefs = [
        data.collateral_percentage,
        data.isk_per_m3,
        data.broker_fee_percentage || 0,
        data.sales_tax_percentage || 0,
        data.desired_roi_percentage || 10,
        data.days_back_for_purchases || 30,
        data.transaction_time_window_minutes || 2,
        data.analysis_period || 60,
        data.aggregate_period || 7,
        data.restock_cadence_days || 7,
      ];
      savedPreferencesRef.current = JSON.stringify(prefs);
      setCollateralPercentage(prefs[0]);
      setIskPerM3(prefs[1]);
      setBrokerFeePercentage(prefs[2]);
      setSalesTaxPercentage(prefs[3]);
      setDesiredRoiPercentage(prefs[4]);
      setDaysBackForPurchases(prefs[5]);
      setTransactionTimeWindowMinutes(prefs[6]);
      setAnalysisPeriod(prefs[7]);
      setAggregatePeriod(prefs[8]);
      setRestockCadenceDays(prefs[9]);
      setPreferencesLoaded(true);
    }
  }, []);

  const fetchSeedingPreferences = useCallback(async () => {
    if (!destinationId) return;

    try {
      const response = await fetchCached(preferencesUrl(destinationId));
      applySeedingPreferences(response.data);
    } catch (error) {
      console.error('Error fetching seeding preferences:', error);
      // If there's an error (like 404 for new destination), still mark as loaded so user can set values
      setPreferencesLoaded(true);
    }
  }, [destinationId, applySeedingPreferences]);

  const updateSeedingPreferences = useCallback(async (collateralPercentage, iskPerM3, brokerFee, salesTax, desiredRoi, daysBack, timeWindow, analysisPeriod, aggregatePeriod, restockCadence) => {
    if (!destinationId) return;

    try {
      await client.post(`/seeding/destinations/${destinationId}/preferences/update/`, {
        collateral_percentage: collateralPercentage,
        isk_per_m3: iskPerM3,
        broker_fee_percentage: brokerFee,
        sales_tax_percentage: salesTax,
        desired_roi_percentage: desiredRoi,
        days_back_for_purchases: daysBack,
        transaction_time_window_minutes: timeWindow,
        analysis_period: analysisPeriod,
        aggregate_period: aggregatePeriod,
        restock_cadence_days: restockCadence
      }, {
        withCredentials: true
      });

      const previous = JSON.parse(savedPreferencesRef.current || '[]');
      savedPreferencesRef.current = JSON.stringify([collateralPercentage, iskPerM3, brokerFee, salesTax, desiredRoi, daysBack, timeWindow, analysisPeriod, aggregatePeriod, restockCadence]);
      // Keep the cached copy current, so reopening this destination doesn't show the old values
      fetchCached(preferencesUrl(destinationId)).catch(() => {});
      // Forecasts depend on both periods; drop cached ones so expanded rows refetch with the saved values
      if (previous[7] !== analysisPeriod || previous[8] !== aggregatePeriod) setItemForecasts({});
      // Trigger a refresh of seeding items by updating a state that will cause useEffect to re-run
      setShouldRefreshItems(prev => prev + 1);
    } catch (error) {
      console.error('Error updating seeding preferences:', error);
    }
  }, [destinationId]);

  const applySeedingItems = useCallback((data) => {
    if (data.success) {
      setDestination(data.destination);
      setPinnedClusterDate(data.destination.price_cluster_date || null);
      setSeedingItems(data.items);
    } else {
      setError(data.error || 'Failed to load seeding items');
    }
  }, []);

  const fetchSeedingItems = useCallback(async () => {
    try {
      const response = await fetchCached(itemsUrl(destinationId));
      applySeedingItems(response.data);
    } catch (error) {
      console.error('Error fetching seeding items:', error);
      if (error.response?.status === 401) {
        navigate('/');
      } else if (error.response?.status === 404) {
        setError('Destination not found');
      } else {
        setError('Failed to load seeding items');
      }
    }
  }, [destinationId, navigate, applySeedingItems]);

  const searchItems = useCallback(async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await client.get(`/items/search/`, {
        params: { q: query, limit: 20 },
        withCredentials: true
      });

      if (response.data.success) {
        setSearchResults(response.data.items);
      }
    } catch (error) {
      console.error('Error searching items:', error);
    }
  }, []);

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!selectedItem) {
      setError('Please select an item');
      return;
    }

    setAddingItem(true);
    setError('');

    try {
      const response = await client.post(
        `/seeding/destinations/${destinationId}/items/add/`,
        {
          type_id: selectedItem.type_id,
          target_stock: targetStock
        }
      );

      if (response.data.success) {
        // Refresh the seeding items list
        await fetchSeedingItems();

        // Reset form
        setShowAddModal(false);
        setItemSearch('');
        setSearchResults([]);
        setSelectedItem(null);
        setTargetStock(0);
      } else {
        setError(response.data.error || 'Failed to add item');
      }
    } catch (error) {
      console.error('Error adding item:', error);
      setError(error.response?.data?.error || 'Failed to add item');
    } finally {
      setAddingItem(false);
    }
  };

  const handleUpdateTargetStock = async (itemId, newStock) => {
    try {
      const response = await client.put(
        `/seeding/destinations/${destinationId}/items/${itemId}/update/`,
        { target_stock: parseInt(newStock) }
      );

      if (response.data.success) {
        // Update the local state
        setSeedingItems(prev =>
          prev.map(item =>
            item.id === itemId
              ? { ...item, target_stock: response.data.target_stock }
              : item
          )
        );
        setEditingStock(null);
        setEditStockValue('');
      } else {
        setError(response.data.error || 'Failed to update target stock');
      }
    } catch (error) {
      console.error('Error updating target stock:', error);
      setError('Failed to update target stock');
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to remove this item from seeding?')) {
      return;
    }

    try {
      const response = await client.delete(
        `/seeding/destinations/${destinationId}/items/${itemId}/remove/`
      );

      if (response.data.success) {
        setSeedingItems(prev => prev.filter(item => item.id !== itemId));
      } else {
        setError(response.data.error || 'Failed to remove item');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      setError('Failed to remove item');
    }
  };

  const handleExportToBuy = async () => {
    try {
      // Get currently filtered and sorted items (respects all filters)
      const currentItems = sortedItems;

      // Calculate items needed to buy
      const itemsToBuy = currentItems
        .map(item => {
          const toBuy = Math.max(0, item.target_stock - item.current_stock);
          return { name: item.item_name, quantity: toBuy };
        })
        .filter(item => item.quantity > 0); // Only include items that need to be bought

      if (itemsToBuy.length === 0) {
        setError('No items need to be bought - all targets are met!');
        return;
      }

      // Format the export text
      const exportText = itemsToBuy
        .map(item => `${item.name} ${item.quantity}`)
        .join('\n');

      // Copy to clipboard
      await navigator.clipboard.writeText(exportText);

      // Show success message briefly
      setError(''); // Clear any existing errors
      const originalButtonText = document.querySelector('.export-buy-btn').textContent;
      const button = document.querySelector('.export-buy-btn');
      button.textContent = 'Copied!';
      setTimeout(() => {
        button.textContent = originalButtonText;
      }, 2000);

    } catch (error) {
      console.error('Error exporting to clipboard:', error);
      setError('Failed to copy to clipboard. Please try again.');
    }
  };

  const fetchBulkForecasts = async () => {
    try {
      const response = await client.get(
        `/seeding/destinations/${destinationId}/items/forecasts/bulk/`
      );

      if (response.data.success) {
        return response.data.forecasts;
      } else {
        throw new Error(response.data.error || 'Failed to fetch bulk forecasts');
      }
    } catch (error) {
      console.error('Error fetching bulk forecasts:', error);
      throw error;
    }
  };

  const handleExportToBuyForecast = async () => {
    try {
      // Show loading state
      const button = document.querySelector('.export-buy-forecast-btn');
      if (button) {
        const originalButtonText = button.textContent;
        button.textContent = '⏳ Loading forecasts...';
        button.disabled = true;

        try {
          // Get currently filtered and sorted items (respects all filters)
          const currentItems = sortedItems;

          // Fetch all forecasts at once
          const forecasts = await fetchBulkForecasts();

          // Calculate items needed to buy based on forecast (only for filtered items)
          const itemsToBuy = currentItems
            .map(item => {
              const forecast = forecasts[item.id];
              const forecastDemand = forecast ? forecast.forecast_demand : 0;
              const toBuy = Math.max(0, Math.ceil(forecastDemand) - item.current_stock);
              return { name: item.item_name, quantity: toBuy };
            })
            .filter(item => item.quantity > 0); // Only include items that need to be bought

          if (itemsToBuy.length === 0) {
            setError('No items need to be bought - all forecast demands are met!');
            button.textContent = originalButtonText;
            button.disabled = false;
            return;
          }

          // Format the export text
          const exportText = itemsToBuy
            .map(item => `${item.name} ${item.quantity}`)
            .join('\n');

          // Copy to clipboard
          await navigator.clipboard.writeText(exportText);

          // Show success message
          setError(''); // Clear any existing errors
          button.textContent = `✓ Copied ${itemsToBuy.length} items!`;
          setTimeout(() => {
            button.textContent = originalButtonText;
            button.disabled = false;
          }, 2000);

        } catch (fetchError) {
          button.textContent = originalButtonText;
          button.disabled = false;
          throw fetchError;
        }
      }

    } catch (error) {
      console.error('Error exporting forecast-based buy list:', error);
      setError('Failed to generate forecast-based buy list. Please try again.');
    }
  };

  const handleExportListPrices = async () => {
    try {
      // Get currently filtered and sorted items (respects all filters and days back setting)
      const currentItems = sortedItems;

      // Filter to only items that have list prices
      const itemsWithListPrices = currentItems.filter(item => item.list_price);

      if (itemsWithListPrices.length === 0) {
        setError('No items have list prices to export. Items need purchased prices to calculate list prices.');
        return;
      }

      // Format the export text: "Item Name Price" format
      const exportText = itemsWithListPrices
        .map(item => {
          const listPrice = parseFloat(item.list_price);
          const formattedPrice = listPrice.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
          return `${item.item_name} ${formattedPrice}`;
        })
        .join('\n');

      // Copy to clipboard
      await navigator.clipboard.writeText(exportText);

      // Show success message briefly
      setError(''); // Clear any existing errors
      const originalButtonText = document.querySelector('.export-list-prices-btn').textContent;
      const button = document.querySelector('.export-list-prices-btn');
      button.textContent = 'Copied!';
      setTimeout(() => {
        button.textContent = originalButtonText;
      }, 2000);
    } catch (error) {
      console.error('Error exporting list prices:', error);
      setError('Failed to copy to clipboard. Please try again.');
    }
  };

  const handleExportSeedList = async () => {
    try {
      const currentItems = sortedItems;

      if (currentItems.length === 0) {
        setError('No items to export.');
        return;
      }

      const exportText = currentItems
        .map(item => `${item.item_name} ${item.target_stock}`)
        .join('\n');

      await navigator.clipboard.writeText(exportText);

      const button = document.querySelector('.export-seed-list-btn');
      const originalText = button.textContent;
      button.textContent = `✓ Copied ${currentItems.length} items`;
      setTimeout(() => { button.textContent = originalText; }, 2000);
    } catch (error) {
      console.error('Error exporting seed list:', error);
      setError('Failed to copy to clipboard. Please try again.');
    }
  };

  const handleImportToSeed = async () => {
    try {
      // Read from clipboard
      const clipboardText = await navigator.clipboard.readText();

      if (!clipboardText.trim()) {
        setError('Clipboard is empty. Please copy items in format "Item Name Number" or "Number Item Name"');
        return;
      }

      // Parse lines from clipboard
      const lines = clipboardText.trim().split('\n').filter(line => line.trim());
      const itemsToImport = [];
      const parseErrors = [];

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        // Try to parse the line - support both "Item Number" and "Number Item" formats
        const parseResult = parseImportLine(trimmedLine);

        if (!parseResult) {
          parseErrors.push(`Could not parse line: "${trimmedLine}"`);
          continue;
        }

        const { itemName, quantity } = parseResult;
        itemsToImport.push({
          item_name: itemName,
          target_stock: quantity
        });
      }

      if (itemsToImport.length === 0) {
        setError('No valid items found to import. Please check the format.');
        return;
      }

      // Make a single batch API call to add all items
      try {
        const response = await client.post(
          `/seeding/destinations/${destinationId}/items/batch-add/`,
          { items: itemsToImport }
        );

        if (!response.data.success) {
          setError(`Batch import failed: ${response.data.error}`);
          return;
        }

        // Process results
        const { results, summary } = response.data;
        const successItems = results.filter(r => r.status === 'success');
        const failedItems = results.filter(r => r.status === 'error');

        // Refresh the seeding items list
        await fetchSeedingItems();

        // Build result message
        const successMessages = successItems.map(item =>
          `✓ ${item.matched_name || item.item_name}: ${item.target_stock}`
        );

        const errorMessages = failedItems.map(item =>
          `✗ ${item.item_name}: ${item.message}`
        );

        // Combine parse errors with API errors
        const allErrors = [...parseErrors, ...errorMessages];

        // Show success message
        if (successMessages.length > 0) {
          console.log(`Imported ${successMessages.length} items:\n${successMessages.join('\n')}`);
        }

        // Show errors if any
        if (allErrors.length > 0) {
          const errorMessage = `Import completed with ${allErrors.length} errors:\n${allErrors.join('\n')}`;
          setError(errorMessage);
        } else {
          setError('');
        }

        // Show temporary success indication
        if (successMessages.length > 0) {
          const button = document.querySelector('.import-seed-btn');
          const originalText = button.textContent;
          button.textContent = `✓ Imported ${successMessages.length}`;
          setTimeout(() => {
            button.textContent = originalText;
          }, 3000);
        }

        // Log summary
        console.log(`Batch import summary: ${summary.succeeded} succeeded, ${summary.failed} failed out of ${summary.total} items`);

      } catch (error) {
        console.error('Error in batch import:', error);
        setError(`Batch import failed: ${error.response?.data?.error || error.message}`);
      }

    } catch (error) {
      console.error('Error importing from clipboard:', error);
      setError('Failed to read from clipboard. Please ensure you have copied the items list.');
    }
  };

  const parseImportLine = (line) => {
    // Remove extra whitespace and normalize
    const cleanLine = line.trim().replace(/\s+/g, ' ');

    // Helper function to parse numbers with commas
    const parseNumber = (numStr) => {
      // Remove commas and parse as integer
      const cleanNumber = numStr.replace(/,/g, '');
      const parsed = parseInt(cleanNumber);
      return isNaN(parsed) ? null : parsed;
    };

    // Try to match "Number Item" format first (e.g., "1,500 Tritanium" or "1500 Tritanium")
    const numberFirstMatch = cleanLine.match(/^([0-9,]+)\s+(.+)$/);
    if (numberFirstMatch) {
      const quantity = parseNumber(numberFirstMatch[1]);
      if (quantity !== null) {
        return {
          quantity: quantity,
          itemName: numberFirstMatch[2].trim()
        };
      }
    }

    // Try to match "Item Number" format (e.g., "Tritanium 1,500" or "Tritanium 1500")
    const numberLastMatch = cleanLine.match(/^(.+)\s+([0-9,]+)$/);
    if (numberLastMatch) {
      const quantity = parseNumber(numberLastMatch[2]);
      if (quantity !== null) {
        return {
          quantity: quantity,
          itemName: numberLastMatch[1].trim()
        };
      }
    }

    // If no number found, assume quantity of 1
    if (cleanLine && !cleanLine.match(/^[0-9,]+$/)) {
      return {
        quantity: 1,
        itemName: cleanLine
      };
    }

    return null;
  };

  const handleSort = (key) => setSortConfig(nextSortConfig(sortConfig, key));

  const filteredItems = useMemo(
    () => filterItems(seedingItems, filters, calculateROI),
    [seedingItems, filters, calculateROI]
  );

  const sortedItems = useMemo(
    () => sortItems(filteredItems, sortConfig, calculateROI),
    [filteredItems, sortConfig, calculateROI]
  );

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '';
    }
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const startEditingStock = (item, value = item.target_stock) => {
    setEditingStock(item.id);
    setEditStockValue(value.toString());
  };

  const cancelEditingStock = () => {
    setEditingStock(null);
    setEditStockValue('');
  };

  const toggleRowExpanded = (itemId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(itemId)) {
      newExpandedRows.delete(itemId);
    } else {
      newExpandedRows.add(itemId);
    }
    setExpandedRows(newExpandedRows);
  };

  const fetchItemForecast = useCallback(async (itemId) => {
    if (itemForecasts[itemId] || loadingForecasts.has(itemId)) return;

    setLoadingForecasts(prev => new Set([...prev, itemId]));
    try {
      const response = await client.get(
        `/seeding/destinations/${destinationId}/items/${itemId}/forecast/`
      );
      if (response.data.success) {
        setItemForecasts(prev => ({ ...prev, [itemId]: response.data.forecast }));
      }
    } catch (error) {
      console.error(`Error fetching forecast for item ${itemId}:`, error);
      setItemForecasts(prev => ({
        ...prev,
        [itemId]: {
          forecast_demand: 0,
          forecast_confidence_lower: 0,
          forecast_confidence_upper: 0,
          forecast_model_type: 'error',
          forecast_best_model: 'Error',
          forecast_data_points: 0
        }
      }));
    } finally {
      setLoadingForecasts(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  }, [destinationId, itemForecasts, loadingForecasts]);

  // Refetch forecasts for open rows after a period change clears the cache
  useEffect(() => {
    expandedRows.forEach((id) => fetchItemForecast(id));
  }, [expandedRows, fetchItemForecast]);

  const selectAnalysisItem = useCallback((item) => {
    setAnalysisItemId(item.id);
    setItemAnalysisCollapsed(false);
  }, []);


  const formatNumber = (number) => {
    if (number === null || number === undefined) return 'N/A';
    return new Intl.NumberFormat().format(number);
  };

  const formatProfitPercentage = (item) => {
    if (!item.source_price || !item.current_price) return 'N/A';

    const roi = calculateROI(item);
    return <span className={`roi-pill ${roi >= 0 ? 'pos' : 'neg'}`}>{roi.toFixed(1)}%</span>;
  };

  // Progress polling function
  const startProgressPolling = (operationType, stateKey, onDone) => {
    // Clear any existing interval
    if (pollingIntervalsRef.current[stateKey]) {
      clearInterval(pollingIntervalsRef.current[stateKey]);
    }

    const interval = setInterval(async () => {
      try {
        const response = await client.get(
          `/seeding/destinations/${destinationId}/progress/${operationType}/`
        );

        if (response.data) {
          setProgressData(prev => ({ ...prev, [stateKey]: response.data }));

          // Stop polling if operation is completed or errored
          if (response.data.status === 'completed' || response.data.status === 'error') {
            clearInterval(interval);
            pollingIntervalsRef.current[stateKey] = null;
            onDone?.(response.data);

            // Clear progress data after a short delay if completed
            if (response.data.status === 'completed') {
              setTimeout(() => {
                setProgressData(prev => ({ ...prev, [stateKey]: null }));
              }, 2000);

              // Refresh seeding items to show updated data
              await fetchSeedingItems();
            }
          }
        }
      } catch (error) {
        // If we get a 404, the operation isn't tracked - stop polling
        if (error.response?.status === 404) {
          clearInterval(interval);
          pollingIntervalsRef.current[stateKey] = null;
          setProgressData(prev => ({ ...prev, [stateKey]: null }));
          onDone?.(null);
        }
      }
    }, 500);

    pollingIntervalsRef.current[stateKey] = interval;
  };

  const handleRefreshMarketData = async () => {
    setRefreshingMarketData(true);
    setError('');

    try {
      // Returns immediately; the fetch runs in the background and the polling
      // completion handler refreshes the items (and last-fetched time)
      await client.post(`/seeding/destinations/${destinationId}/refresh-market-data/`, {});
      startProgressPolling('market_data', 'marketData', (progress) => {
        setRefreshingMarketData(false);
        if (progress?.status === 'error') {
          setError(progress.error_message || 'Failed to refresh market data');
        }
      });
    } catch (error) {
      console.error('Error refreshing market data:', error);
      setError(error.response?.status === 429 ? error.response.data.error : 'Failed to refresh market data');
      setRefreshingMarketData(false);
    }
  };

  const handleRefreshUserOrders = async () => {
    setRefreshingUserOrders(true);
    setError('');

    // Start progress polling
    startProgressPolling('user_orders', 'userOrders');

    try {
      const response = await client.post(
        `/seeding/destinations/${destinationId}/refresh-user-orders/`,
        {}
      );

      if (response.data.success) {
        // Update the destination data
        setDestination(prev => ({
          ...prev,
          user_orders_last_fetched: response.data.last_fetched
        }));

        // Note: fetchSeedingItems will be called by polling completion handler
      } else {
        setError(response.data.error || 'Failed to refresh user orders');
      }
    } catch (error) {
      console.error('Error refreshing user orders:', error);
      if (error.response?.status === 429) {
        setError(error.response.data.error);
      } else {
        setError('Failed to refresh user orders');
      }
    } finally {
      setRefreshingUserOrders(false);
    }
  };

  const handleRefreshWalletTransactions = async () => {
    setRefreshingWalletTransactions(true);
    setError('');

    // Start progress polling
    startProgressPolling('wallet_transactions', 'walletTransactions');

    try {
      const response = await client.post(
        `/seeding/destinations/${destinationId}/refresh-wallet-transactions/`,
        {}
      );

      if (response.data.success) {
        setError(''); // Clear any previous errors

        // Update the destination data with new timestamp
        setDestination(prev => ({
          ...prev,
          wallet_transactions_last_fetched: new Date().toISOString()
        }));

        console.log('Wallet transactions refreshed:', response.data.message);
      } else {
        setError(response.data.error || 'Failed to refresh wallet transactions');
      }
    } catch (error) {
      console.error('Error refreshing wallet transactions:', error);
      if (error.response?.status === 429) {
        setError(error.response.data.error);
      } else {
        setError('Failed to refresh wallet transactions');
      }
    } finally {
      setRefreshingWalletTransactions(false);
    }
  };

  const openClusterModal = async () => {
    setLoadingClusters(true);
    setShowClusterModal(true);
    try {
      const response = await client.get(
        `/seeding/destinations/${destinationId}/purchase-clusters/`,
        { withCredentials: true }
      );
      setClusters(response.data.clusters || []);
      setPinnedClusterDate(response.data.pinned_cluster_date || null);
    } catch (err) {
      console.error('Error loading purchase clusters:', err);
    } finally {
      setLoadingClusters(false);
    }
  };

  const selectCluster = async (clusterDate) => {
    try {
      await client.post(
        `/seeding/destinations/${destinationId}/set-price-cluster/`,
        { cluster_date: clusterDate },
        { withCredentials: true }
      );
      setPinnedClusterDate(clusterDate);
      setShowClusterModal(false);
      // Refresh items so purchased prices update immediately
      setShouldRefreshItems(prev => prev + 1);
    } catch (err) {
      console.error('Error setting price cluster:', err);
      setError('Failed to save cluster selection');
    }
  };

  const handleFetchRegionStats = async () => {
    setRefreshingRegionStats(true);
    setError('');

    // Start progress polling
    startProgressPolling('region_stats', 'regionStats');

    try {
      const response = await client.post(
        `/seeding/destinations/${destinationId}/fetch-region-stats/`,
        {}
      );

      if (response.data.success) {
        setError(''); // Clear any previous errors
        console.log('Region statistics fetched:', response.data.message);
      } else {
        setError(response.data.error || 'Failed to fetch region statistics');
      }
    } catch (error) {
      console.error('Error fetching region statistics:', error);
      if (error.response?.status === 429) {
        setError(error.response.data.error);
      } else {
        setError('Failed to fetch region statistics');
      }
    } finally {
      setRefreshingRegionStats(false);
    }
  };

  const loadRegionOverview = useCallback(async () => {
    setRegionOverviewLoading(true);
    try {
      const params = {
        days: regionOverviewDays,
        sort: regionOverviewSort.col,
        order: regionOverviewSort.order,
        page: regionOverviewPage,
        page_size: 50,
      };
      if (regionOverviewDebouncedSearch) params.search = regionOverviewDebouncedSearch;
      const response = await client.get(
        `/seeding/destinations/${destinationId}/all-region-stats/`,
        { params }
      );
      setRegionOverviewData(response.data);
    } catch (err) {
      console.error('Error loading region overview:', err);
    } finally {
      setRegionOverviewLoading(false);
    }
  }, [destinationId, regionOverviewDays, regionOverviewSort, regionOverviewPage, regionOverviewDebouncedSearch]);

  const handleFetchAllRegionTypes = async () => {
    setRefreshingAllRegionTypes(true);
    setError('');

    try {
      const response = await client.post(
        `/seeding/destinations/${destinationId}/fetch-all-region-types/`,
        {}
      );
      if (!response.data.success) {
        setError(response.data.error || 'Failed to start region type fetch');
        setRefreshingAllRegionTypes(false);
        return;
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setError(err.response.data.error);
      } else {
        setError('Failed to start region type fetch');
      }
      setRefreshingAllRegionTypes(false);
      return;
    }

    // POST returned immediately — poll for background task completion
    if (pollingIntervalsRef.current.allRegionTypes) {
      clearInterval(pollingIntervalsRef.current.allRegionTypes);
    }

    const interval = setInterval(async () => {
      try {
        const pollResp = await client.get(
          `/seeding/destinations/${destinationId}/progress/all_region_types/`
        );
        if (pollResp.data) {
          setProgressData(prev => ({ ...prev, allRegionTypes: pollResp.data }));
          if (pollResp.data.status === 'completed' || pollResp.data.status === 'error') {
            clearInterval(interval);
            pollingIntervalsRef.current.allRegionTypes = null;
            setRefreshingAllRegionTypes(false);
            if (pollResp.data.status === 'completed') {
              setTimeout(() => setProgressData(prev => ({ ...prev, allRegionTypes: null })), 2000);
              loadRegionOverview();
            }
          }
        }
      } catch (pollErr) {
        if (pollErr.response?.status === 404) {
          clearInterval(interval);
          pollingIntervalsRef.current.allRegionTypes = null;
          setProgressData(prev => ({ ...prev, allRegionTypes: null }));
          setRefreshingAllRegionTypes(false);
        }
      }
    }, 1000);

    pollingIntervalsRef.current.allRegionTypes = interval;
  };

  // Show a destination opened earlier in this tab straight away, before the first paint;
  // the effect below then replaces it with fresh data
  const showingCachedRef = useRef(false);
  useLayoutEffect(() => {
    const cached = [USER_URL, itemsUrl(destinationId), preferencesUrl(destinationId)].map(getCached);
    showingCachedRef.current = cached.every(Boolean);
    if (!showingCachedRef.current) return;
    applyUserInfo(cached[0].data);
    applySeedingItems(cached[1].data);
    applySeedingPreferences(cached[2].data);
    setLoading(false);
  }, [destinationId, applyUserInfo, applySeedingItems, applySeedingPreferences]);

  // Load user info and seeding items on mount, in parallel to save a round trip.
  // A logged-out visitor still sees the login prompt: it renders before any items error.
  useEffect(() => {
    const loadData = async () => {
      if (!showingCachedRef.current) setLoading(true);
      await Promise.all([
        fetchUserInfo(),
        fetchSeedingItems(),
        fetchSeedingPreferences()
      ]);
      setLoading(false);
    };

    loadData();
  }, [fetchUserInfo, fetchSeedingItems, fetchSeedingPreferences]);

  // Search items when search query changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchItems(itemSearch);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [itemSearch, searchItems]);

  // Update seeding preferences when values change (debounced to avoid excessive API calls)
  useEffect(() => {
    const prefs = [collateralPercentage, iskPerM3, brokerFeePercentage, salesTaxPercentage, desiredRoiPercentage, daysBackForPurchases, transactionTimeWindowMinutes, analysisPeriod, aggregatePeriod, restockCadenceDays];
    if (user && preferencesLoaded && JSON.stringify(prefs) !== savedPreferencesRef.current) {
      const timeoutId = setTimeout(() => {
        updateSeedingPreferences(...prefs);
      }, 750); // 750ms delay

      return () => clearTimeout(timeoutId);
    }
  }, [collateralPercentage, iskPerM3, brokerFeePercentage, salesTaxPercentage, desiredRoiPercentage, daysBackForPurchases, transactionTimeWindowMinutes, analysisPeriod, aggregatePeriod, restockCadenceDays, user, preferencesLoaded, updateSeedingPreferences]);

  // Refresh seeding items in the background when preferences are updated, keeping the page on screen
  useEffect(() => {
    if (shouldRefreshItems > 0 && user && destinationId) {
      fetchSeedingItems();
    }
  }, [shouldRefreshItems, user, destinationId, fetchSeedingItems]);

  // Debounce region overview search input
  useEffect(() => {
    const timeout = setTimeout(() => {
      setRegionOverviewDebouncedSearch(regionOverviewSearch);
      setRegionOverviewPage(1);
    }, 400);
    return () => clearTimeout(timeout);
  }, [regionOverviewSearch]);

  // regionOverviewData excluded from deps intentionally — including it would cause an infinite loop
  // since loadRegionOverview calls setRegionOverviewData.
  useEffect(() => {
    if (regionOverviewData !== null) {
      loadRegionOverview();
    }
  }, [loadRegionOverview]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup polling intervals on unmount
  useEffect(() => {
    // Capture ref value at effect creation time for the cleanup closure
    const intervals = pollingIntervalsRef.current;
    return () => {
      Object.values(intervals).forEach(interval => {
        if (interval) clearInterval(interval);
      });
    };
  }, []);

  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Seeding" />
        <div className="page-status">Loading seeding data...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="page">
        <PageHeader title="Seeding" />
        <div className="page-status">Log in with your EVE Online character to use the seeding tools.</div>
      </div>
    );
  }

  if (error && !destination) {
    return (
      <div className="page">
        <PageHeader title="Destination" crumb={{ to: '/seeding', label: 'Seeding' }} />
        <div className="page-status error">{error}</div>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title={destination?.name}
        crumb={{ to: '/seeding', label: 'Seeding' }}
        meta={destination && [destination.system, destination.region, destination.is_structure ? 'Structure' : 'Station']}
      />
      <div className="page-content">
          {destination && (
            <DataRefreshControls
              destination={destination}
              refreshingMarketData={refreshingMarketData}
              refreshingUserOrders={refreshingUserOrders}
              refreshingWalletTransactions={refreshingWalletTransactions}
              refreshingRegionStats={refreshingRegionStats}
              handleRefreshMarketData={handleRefreshMarketData}
              handleRefreshUserOrders={handleRefreshUserOrders}
              handleRefreshWalletTransactions={handleRefreshWalletTransactions}
              handleFetchRegionStats={handleFetchRegionStats}
              progressData={progressData}
            />
          )}

          {destination && (
            <SeedingPreferences
              collateralPercentage={collateralPercentage}
              setCollateralPercentage={setCollateralPercentage}
              iskPerM3={iskPerM3}
              setIskPerM3={setIskPerM3}
              brokerFeePercentage={brokerFeePercentage}
              setBrokerFeePercentage={setBrokerFeePercentage}
              salesTaxPercentage={salesTaxPercentage}
              setSalesTaxPercentage={setSalesTaxPercentage}
              sccPercentage={sccPercentage}
              desiredRoiPercentage={desiredRoiPercentage}
              setDesiredRoiPercentage={setDesiredRoiPercentage}
              daysBackForPurchases={daysBackForPurchases}
              setDaysBackForPurchases={setDaysBackForPurchases}
              transactionTimeWindowMinutes={transactionTimeWindowMinutes}
              setTransactionTimeWindowMinutes={setTransactionTimeWindowMinutes}
              analysisPeriod={analysisPeriod}
              setAnalysisPeriod={setAnalysisPeriod}
              aggregatePeriod={aggregatePeriod}
              setAggregatePeriod={setAggregatePeriod}
              restockCadenceDays={restockCadenceDays}
              setRestockCadenceDays={setRestockCadenceDays}
              setError={setError}
              clusterLabel={pinnedClusterDate ? new Date(pinnedClusterDate).toLocaleString() : 'Auto (most recent)'}
              onPickCluster={openClusterModal}
            />
          )}

          {error && <div className="error-message">{error}</div>}

          <ItemStockingTable
            collapsed={seedingItemsCollapsed}
            onToggle={() => setSeedingItemsCollapsed(!seedingItemsCollapsed)}
            seedingItems={seedingItems}
            sortedItems={sortedItems}
            filters={filters}
            setFilters={setFilters}
            page={itemPage}
            setPage={setItemPage}
            sortConfig={sortConfig}
            handleSort={handleSort}
            getSortIcon={getSortIcon}
            editingStock={editingStock}
            editStockValue={editStockValue}
            setEditStockValue={setEditStockValue}
            startEditingStock={startEditingStock}
            cancelEditingStock={cancelEditingStock}
            handleUpdateTargetStock={handleUpdateTargetStock}
            handleRemoveItem={handleRemoveItem}
            formatPrice={formatPrice}
            formatNumber={formatNumber}
            formatProfitPercentage={formatProfitPercentage}
            handleImportToSeed={handleImportToSeed}
            handleExportSeedList={handleExportSeedList}
            handleExportToBuy={handleExportToBuy}
            handleExportToBuyForecast={handleExportToBuyForecast}
            handleExportListPrices={handleExportListPrices}
            aggregatePeriod={aggregatePeriod}
            setShowAddModal={setShowAddModal}
            expandedRows={expandedRows}
            toggleRowExpanded={toggleRowExpanded}
            itemForecasts={itemForecasts}
            loadingForecasts={loadingForecasts}
            fetchItemForecast={fetchItemForecast}
            analysisItemId={analysisItemId}
            onSelectItem={selectAnalysisItem}
          />

          <ItemAnalysisSection
            destinationId={destinationId}
            item={seedingItems.find((i) => i.id === analysisItemId)}
            analysisPeriod={analysisPeriod}
            setAnalysisPeriod={setAnalysisPeriod}
            aggregatePeriod={aggregatePeriod}
            formatNumber={formatNumber}
            formatPrice={formatPrice}
            collapsed={itemAnalysisCollapsed}
            onToggle={() => setItemAnalysisCollapsed(!itemAnalysisCollapsed)}
          />

          <MarkupOpportunitiesSection
            destinationId={destinationId}
            refreshKey={seedingItems}
            onItemAdded={fetchSeedingItems}
          />

          <RegionIskVolumeSection path={`/seeding/destinations/${destinationId}/region-isk-volume/`} />

          <CollapsiblePanel
            title="Region market overview"
            collapsed={regionOverviewCollapsed}
            onToggle={() => {
              const opening = regionOverviewCollapsed;
              setRegionOverviewCollapsed(!regionOverviewCollapsed);
              if (opening && regionOverviewData === null && !refreshingAllRegionTypes) {
                loadRegionOverview();
              }
            }}
            actions={(
              <>
                <select
                  className="region-days-selector"
                  value={regionOverviewDays}
                  onChange={e => {
                    setRegionOverviewDays(Number(e.target.value));
                    setRegionOverviewPage(1);
                  }}
                  aria-label="Time window"
                >
                  {[7, 14, 30, 60, 90].map(d => (
                    <option key={d} value={d}>Last {d} days</option>
                  ))}
                </select>
                <button
                  className="btn"
                  onClick={handleFetchAllRegionTypes}
                  disabled={refreshingAllRegionTypes}
                >
                  {refreshingAllRegionTypes ? 'Fetching...' : 'Fetch all region types'}
                </button>
              </>
            )}
          >
            <div className="region-overview-content">
              {refreshingAllRegionTypes && progressData.allRegionTypes && (
                <div className="progress-container">
                  <div className="progress-message">{progressData.allRegionTypes.message}</div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progressData.allRegionTypes.progress_percentage || 0}%` }}
                    />
                  </div>
                  {progressData.allRegionTypes.total > 0 && (
                    <div className="progress-detail">
                      {progressData.allRegionTypes.current} / {progressData.allRegionTypes.total}
                    </div>
                  )}
                </div>
              )}
              {regionOverviewLoading && regionOverviewData === null ? (
                <p className="region-overview-empty">Loading…</p>
              ) : regionOverviewData ? (
                <RegionMarketStatsTable
                  data={regionOverviewData}
                  sort={regionOverviewSort}
                  search={regionOverviewSearch}
                  page={regionOverviewPage}
                  loading={regionOverviewLoading}
                  onSearch={v => setRegionOverviewSearch(v)}
                  onSort={col => {
                    setRegionOverviewSort(prev =>
                      prev.col === col
                        ? { col, order: prev.order === 'desc' ? 'asc' : 'desc' }
                        : { col, order: 'desc' }
                    );
                    setRegionOverviewPage(1);
                  }}
                  onPageChange={setRegionOverviewPage}
                />
              ) : (
                !refreshingAllRegionTypes && (
                  <p className="region-overview-empty">
                    No region stats for this time window. Use "Fetch all region types" to load market data for every item in this region.
                  </p>
                )
              )}
            </div>
          </CollapsiblePanel>

        <AddItemModal
          showAddModal={showAddModal}
          setShowAddModal={setShowAddModal}
          handleAddItem={handleAddItem}
          itemSearch={itemSearch}
          setItemSearch={setItemSearch}
          searchResults={searchResults}
          setSearchResults={setSearchResults}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          targetStock={targetStock}
          setTargetStock={setTargetStock}
          addingItem={addingItem}
          error={error}
          setError={setError}
        />

        {showClusterModal && (
          <div className="modal-overlay" onClick={() => setShowClusterModal(false)}>
            <div className="modal-content cluster-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Select Purchase Cluster</h3>
                <button className="icon-btn modal-close" aria-label="Close" onClick={() => setShowClusterModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <p className="cluster-modal-hint">
                  Choose which shopping run to use as the cost basis for this destination.
                  The most recent cluster is used by default.
                </p>
                {loadingClusters ? (
                  <div className="cluster-loading">Loading clusters...</div>
                ) : clusters.length === 0 ? (
                  <div className="cluster-empty">No purchase clusters found in the configured lookback window.</div>
                ) : (
                  <div className="cluster-list">
                    <div
                      className={`cluster-item${!pinnedClusterDate ? ' cluster-item--selected' : ''}`}
                      onClick={() => selectCluster(null)}
                    >
                      <div className="cluster-item-date">Auto (most recent)</div>
                      <div className="cluster-item-meta">Always uses the newest cluster</div>
                    </div>
                    {clusters.filter(c => c.matching_item_count > 0).map((cluster, idx) => {
                      const isSelected = pinnedClusterDate === cluster.cluster_date;
                      return (
                        <div
                          key={idx}
                          className={`cluster-item${isSelected ? ' cluster-item--selected' : ''}`}
                          onClick={() => selectCluster(cluster.cluster_date)}
                        >
                          <div className="cluster-item-date">
                            {new Date(cluster.cluster_date).toLocaleString()}
                          </div>
                          <div className="cluster-item-meta">
                            {cluster.matching_item_count} of your items &middot; {cluster.total_transactions} total txns
                          </div>
                          {cluster.matching_items.length > 0 && (
                            <div className="cluster-item-prices">
                              {cluster.matching_items.slice(0, 5).map(item => (
                                <span key={item.type_id} className="cluster-price-tag">
                                  {item.name}: {parseFloat(item.price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              ))}
                              {cluster.matching_items.length > 5 && (
                                <span className="cluster-price-tag cluster-price-tag--more">
                                  +{cluster.matching_items.length - 5} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SeedingDetailPage;